/**
 * Russian display formatting shared by the web app and the server (decisions-log V1): a no-break
 * space for thousands and a comma for decimals. Invoice template variables never use these (see
 * invoice-template.ts) and exports write plain dot decimals (L7).
 */
import { FX_SCALE, RATE_SCALE } from './money';
import { MONTHS_SHORT_RU } from './ru';

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat('ru-RU', options);
    numberFormats.set(key, format);
  }
  return format;
}

const decimalComma = (text: string): string => text.replace('.', ',');

/** 312843 → "3 128,43" */
export const formatMoney = (minor: number): string =>
  numberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100);

/** 312843, "USD" → "3 128,43 USD" */
export const formatMoneyCcy = (minor: number, currency: string): string => `${formatMoney(minor)} ${currency}`;

/** 312843, "USD" → "USD 3 128,43" */
export const formatCcyMoney = (minor: number, currency: string): string => `${currency} ${formatMoney(minor)}`;

/** Rounded whole units for matrices: 312843 → "3 128" */
export const formatMoneyWhole = (minor: number): string =>
  numberFormat({ maximumFractionDigits: 0 }).format(Math.round(minor / 100));

/** 7050 minutes → "117,5"; one decimal like the design's hrs(). */
export const formatHours = (minutes: number): string =>
  numberFormat({ maximumFractionDigits: 1 }).format(Math.round((minutes / 60) * 10) / 10);

/** 266250 → "26,625", 360000 → "36,00" (design rt()). */
export const formatRate = (rateE4: number): string =>
  numberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(rateE4 / RATE_SCALE);

/** 2684500 → "2,6845" */
export const formatFx = (rateE6: number, digits = 4): string => decimalComma((rateE6 / FX_SCALE).toFixed(digits));

/** Plain editable value for inputs: 312843 → "3128,43", 604800 → "6048,00". */
export const minorToInput = (minor: number): string => decimalComma((minor / 100).toFixed(2));

/** 7050 → "117,5", 10080 → "168" */
export const minutesToInput = (minutes: number): string =>
  decimalComma(String(Math.round((minutes / 60) * 100) / 100));

/** 266250 → "26,625" */
export const rateToInput = (rateE4: number): string => decimalComma(String(rateE4 / RATE_SCALE));

/** 30.56 → "30,6 %" */
export const formatPercent = (value: number, digits = 1): string => `${decimalComma(value.toFixed(digits))}\u00a0%`;

/** Standalone short month label for index 0..11: "Сен". */
export const monthShort = (index: number): string => MONTHS_SHORT_RU[index] ?? '';
